import {formatTime, Temporal} from "@/shared/utils/datefunctions";
import * as Button from "@/shared/utils/button";
import {TaskDropdown} from "@/shared/task/taskDropdown";
import {isWorkingTimeIncluded} from "@/features/timeline/recordingProcedure";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import * as Input from "@/shared/utils/input";
import {setSwitcheryState} from "@/shared/utils/misc";
import {DOMUtil} from "@/shared/utils/dom";
import {CombinedRecordingStatus, Task, WorkingTimeType} from "@/shared/timr";
import {RecordingMode, RecordingProcedure, WorkingTimeTypeCategory} from "@/shared/timrTypes";
import {EVENT_RECOMMENDED_TASK_SELECTED, loadAndDisplayRecommendedTasks} from "@/shared/task/taskRecommendationHelper";

export interface SwitchResumeCombinedRecordingDialogConfig {
	container: JQuery;
	workingTimeTypes: WorkingTimeType[];
	recordingMode: RecordingMode;
}

type SwitchResumeDialogMode = "switch" | "resume";

export class SwitchResumeCombinedRecordingDialog {

	public static readonly EVENT_CLOSED = "closed";
	private dialog: BootstrapModal.Modal;
	private taskDropdown: TaskDropdown;
	private workingTimeTypeSelect: JQuery;
	private selectedTask: Task | null = null;
	private endTime: Temporal.ZonedDateTime | null;
	private procedure: RecordingProcedure;
	private dialogMode: SwitchResumeDialogMode = "switch";
	private taskSwitchExecutor: (workingTimeTypeId: number | null, taskId: number | null) => JQuery.jqXHR<CombinedRecordingStatus>;

	constructor(public config: SwitchResumeCombinedRecordingDialogConfig) {
		this.init();
	}

	public show(
		procedure: RecordingProcedure,
		endTime: Temporal.ZonedDateTime | null,
		taskSwitchExecutor: (workingTimeTypeId: number | null, taskId: number | null) => JQuery.jqXHR<CombinedRecordingStatus>,
		preselectWorkingTimeType: WorkingTimeType | null,
		dialogMode: SwitchResumeDialogMode
	): void {
		this.procedure = procedure;
		this.endTime = endTime;
		this.dialogMode = dialogMode;
		this.taskSwitchExecutor = taskSwitchExecutor;

		this.updateDialogState(preselectWorkingTimeType);
		this.dialog.show();
	}

	/**
	 * Updates the state of the dialog based on the specified working time type and other configurations.
	 *
	 * @param {WorkingTimeType} [preselectWorkingTimeType] - An optional parameter representing the working time type to be preselected
	 * if it exists in the current selectable options.
	 *
	 * @return {void} This method does not return any value.
	 */
	private updateDialogState(preselectWorkingTimeType: WorkingTimeType | null): void {
		this.config.container.attr("data-procedure", this.procedure.toString());

		this.resetSelectedTask();
		this.taskDropdown.reset();
		this.taskDropdown.queryIfNeeded();
		this.unselectRecommendedTasks();

		this.dialog.setTitle(this.getDialogTitle());

		if (this.config.recordingMode !== RecordingMode.STRICT) {
			setSwitcheryState(this.config.container.find("#toggleProjectTimeSwitchery"), this.procedure === RecordingProcedure.Both);
		}

		this.config.container.find("[data-working-time-section]").toggle(isWorkingTimeIncluded(this.procedure));

		DOMUtil.fillWorkingTimeTypeSelect(this.workingTimeTypeSelect, this.config.workingTimeTypes, false);

		// preselect working time type if provided and available in the current selectable types
		if (preselectWorkingTimeType && DOMUtil.hasOption(this.workingTimeTypeSelect, preselectWorkingTimeType.id.toString())) {
			this.workingTimeTypeSelect.val(preselectWorkingTimeType.id);
		}

		// update state of the dialog (e.g. visibility of project time switch or task selection) based on the selected procedure and working time type
		this.updateState();
	}

	/**
	 * Updates the state of the dialog based on the selected working time type and current recording procedure<br>
	 * It adjusts the visibility of various UI elements such as the start project time option, non-attendance time info, and task selection
	 */
	private updateState(): void {
		const isAttendanceTimeSelected = this.isAttendanceTimeSelected();

		// show info that with selected non-attendance time it's not possible to switch/start project time
		this.config.container.find("[data-non-attendance-time]").toggle(this.procedure !== RecordingProcedure.ProjectTime && !isAttendanceTimeSelected);

		// show option to start project time if only working time is running (not available in strict mode)
		this.config.container.find("[data-toggle-project-time]").toggle(
			this.config.recordingMode !== RecordingMode.STRICT && this.procedure !== RecordingProcedure.ProjectTime && isAttendanceTimeSelected);

		// show task selection if project time is included (can also be displayed on clicking "start project time" switch or STRICT mode is active)
		this.config.container.find("[data-task-selection]")
			.toggle(this.procedure === RecordingProcedure.ProjectTime ||
				isAttendanceTimeSelected && (this.config.container.find("#toggleProjectTimeSwitchery").is(":checked") || this.config.recordingMode === RecordingMode.STRICT)
			);
	}

	private getSelectedWorkingTimeType(): WorkingTimeType {
		const workingTimeTypeId = parseInt(this.config.container.find("select[name=workingTimeTypeId]").val() as string, 10);
		return this.config.workingTimeTypes.find(wtt => wtt.id === workingTimeTypeId) ?? null;
	}

	/**
	 * Connects the current task dropdown with another task dropdown.
	 * This allows synchronization between the two dropdowns.
	 *
	 * @param otherTaskDropdown - The other TaskDropdown instance to connect with.
	 */
	public connect(otherTaskDropdown: TaskDropdown): void {
		TaskDropdown.connect(this.taskDropdown, otherTaskDropdown);
	}

	private init(): void {
		this.dialog = new BootstrapModal.Modal({
			title: "switchTaskDialog.title",
			message: this.config.container,
			size: "modal-lg",
			destroyOnClose: false,
			buttons: [{
				text: "dialog.switch",
				action: () => {
					this.executeSwitchAction();
				},
				css: "switchButton btn-primary"
			}, {
				text: "dialog.resume",
				action: () => {
					this.executeSwitchAction();
				},
				css: "resumeButton btn-primary"
			}, {
				text: "dialog.cancel",
				css: "cancelButton btn-default"
			}],
			closeOnOutsideClick: false,
			onClose: () => {
				// Only remove selected listener to not remove the connection listener
				$(this.taskDropdown).off(TaskDropdown.EVENT_SELECTED);
				$(this).triggerHandler(SwitchResumeCombinedRecordingDialog.EVENT_CLOSED);
				this.taskDropdown.closeDropdown();

				Input.enable(this.dialog.getButtons("cancelButton"));
				Input.enable(this.dialog.getButtons("switchButton"));
				Input.enable(this.dialog.getButtons("resumeButton"));

				if (this.config.recordingMode !== RecordingMode.STRICT) {
					setSwitcheryState(this.config.container.find("#toggleProjectTimeSwitchery"), false);
				}

				this.resetSelectedTask();
			},
			onOpen: () => {
				this.setLoadingState(false);
				this.config.container.find("[data-task-query]").focus();
				this.bindTaskDropdown();
				this.bindRecommendedTasks();
			}
		});

		$(this.dialog).on("show.bs.modal", () => {
			this.updateActionButtonsVisibility();
			void loadAndDisplayRecommendedTasks(
				$(this.dialog),
				this.config.container.find("[data-recommended-tasks]"),
				this.config.container.find("[data-recent-tasks]"),
				this.endTime
			);
		});

		this.taskDropdown = new TaskDropdown({
			rootElement: this.config.container.find("#projectTimeSwitchTaskDropdown"),
			allowSelectionOfNotBookable: false,
			hideInactive: true,
			emptyMessage: ""
		}, {
			loadRootNodesURL: "recording/loadAllTasks.json",
			loadSubNodesURL: "recording/treeLoad.json",
			filterNodesURL: "recording/filterTasks.json"
		});

		this.config.container.find("[data-task-query]")
			.on("input", () => this.resetSelectedTask())
			.on("keydown", (e) => {
				if (e.key === "Escape") {
					this.dialog.close();
					return false;
				}
			});

		this.config.container.find("#toggleProjectTimeSwitchery").closest("label").on("click", (e) => {
			const configureTypes = $(e.target).is(":checked");
			this.config.container.find("[data-task-selection]").slideToggleBool(configureTypes, 100);
		});

		this.workingTimeTypeSelect = this.config.container.find("select[name=workingTimeTypeId]");
		DOMUtil.fillWorkingTimeTypeSelect(this.workingTimeTypeSelect, this.config.workingTimeTypes, false);
		this.workingTimeTypeSelect.change(() => this.updateState());
	}

	/**
	 * Executes the switching action for combined recording based on the provided configuration and UI elements.
	 */
	private executeSwitchAction(): void {
		this.switchCombinedRecording(
			this.procedure,
			this.config.container.find("#workingTimeSwitchWorkingTimeTypeId").val() as number,
			this.selectedTask?.id ?? null,
			this.isToggleProjectTimeChecked()
		);
	}

	private resetSelectedTask(): void {
		this.selectedTask = null;
		this.taskDropdown.setSelectedTask(null);
		this.config.container.find(".fancytree-node").removeClass("fancytree-active");
	}

	/**
	 * Checks if the selected working time type is of category AttendanceTime.
	 *
	 * @returns {boolean} True if the selected working time type is of category AttendanceTime, otherwise false.
	 */
	private isAttendanceTimeSelected(): boolean {
		return this.getSelectedWorkingTimeType()?.category === WorkingTimeTypeCategory.AttendanceTime;
	}

	/**
	 * Unselects all recommended and recent tasks of the by removing the 'selected' class (left side)
	 */
	private unselectRecommendedTasks(): void {
		this.config.container.find("[data-recent-tasks] > .panel-task, [data-recommended-tasks] > .panel-task").removeClass("selected");
	}

	/**
	 * Binds the task dropdown to handle task selection events.
	 * When a task is selected, it updates the dialog state based on the current procedure.
	 * If the procedure is ProjectTime, it switches the project time directly.
	 * Otherwise, it sets the selected task ID for a later switch (and unselects recommended tasks).
	 */
	private bindTaskDropdown(): void {
		this.setLoadingState(false);
		$(this.taskDropdown).on(TaskDropdown.EVENT_SELECTED, (e, task: Task) => {
			if (this.isModeProjectTime()) {
				// avoid triggering to double events
				$(this.taskDropdown).off(TaskDropdown.EVENT_SELECTED);

				// directly switch project time
				this.setLoadingState(true);
				this.switchProjectTime(task.id);
			} else {
				// reset recommended task selection
				this.unselectRecommendedTasks();

				// select new task for the switch button click later
				this.selectedTask = task;
			}
		});
	}

	/**
	 * Binds the recommended tasks to handle task selection events.
	 * When a recommended task is selected, it updates the dialog state based on the current procedure.
	 * If the procedure is ProjectTime, it switches the project time.
	 * Otherwise, it sets the selected task ID for a later switch (and unselects the task dropdown and recommended tasks).
	 */
	private bindRecommendedTasks(): void {
		this.setLoadingState(false);
		$(this.dialog)
			.off(EVENT_RECOMMENDED_TASK_SELECTED)
			.on(EVENT_RECOMMENDED_TASK_SELECTED, (e, task: Task, currentTarget: JQuery) => {
				if (this.isModeProjectTime()) {
					// Only remove selected listener to not remove the connection listener
					$(this.taskDropdown).off(TaskDropdown.EVENT_SELECTED);
					this.setLoadingState(true);

					// directly switch project time
					this.switchProjectTime(task.id);
				} else {
					// reset current task and recommended task selection
					this.taskDropdown.setSelectedTask(null);
					this.config.container.find(".fancytree-node").removeClass("fancytree-active");
					this.unselectRecommendedTasks();

					// select new task for the switch button click later
					currentTarget.addClass("selected");
					this.selectedTask = task;
				}
			});
	}

	/**
	 * Sets the loading state of the dialog.
	 * Disables or enables input fields and toggles the loading state of the cancel button.
	 *
	 * @param setLoading - If true, sets the dialog to loading state, otherwise the loading state gets removed.
	 */
	private setLoadingState(setLoading: boolean): void {
		// disable input fields
		const inputFields = this.config.container.find("input:not([type='checkbox']), select");
		inputFields.prop("disabled", setLoading);

		// disable task selection
		this.config.container.find("ul.fancytree-container").toggleClass("ui-fancytree-disabled", setLoading);

		// set state of action buttons (bottom right)
		const cancelButton = this.dialog.getButtons("cancelButton");
		if (this.isModeProjectTime()) {
			if (setLoading) {
				Button.setLoading(cancelButton)
			} else {
				Button.resetLoading(cancelButton);
			}
		} else {
			const actionButtonClass = this.isResumeMode() ? "resumeButton" : "switchButton";
			const actionButton = this.dialog.getButtons(actionButtonClass);
			if (setLoading) {
				Button.setLoading(actionButton);
			} else {
				Button.resetLoading(actionButton);
			}
			Input.toggle(cancelButton, !setLoading);
		}
	}

	/**
	 * Checks whether the "Toggle Project Time" switch is currently checked.
	 * @return {boolean} True if the "Toggle Project Time" switch is checked, otherwise false.
	 */
	private isToggleProjectTimeChecked(): boolean {
		return this.config.container.find("#toggleProjectTimeSwitchery").is(":checked");
	}

	/**
	 * Determines if the current mode is set to "Project Time".
	 *
	 * @return {boolean} True if the procedure is "Project Time", otherwise false.
	 */
	private isModeProjectTime(): boolean {
		return this.procedure === RecordingProcedure.ProjectTime;
	}

	/**
	 * Switches the recording mode to Project Time for the specified task.
	 *
	 * @param {number} taskId - The identifier of the task for which the Project Time recording is to be switched.
	 * @return {void} - This method does not return a value.
	 */
	private switchProjectTime(taskId: number): void {
		this.switchCombinedRecording(RecordingProcedure.ProjectTime, null, taskId, null);
	}

	/**
	 * Toggles combined recording based on the provided procedure, working time type, task ID, and toggle project time flag.
	 *
	 * @param {RecordingProcedure} procedure - The procedure type to be switched for combined recording.
	 * @param {number | null} workingTimeTypeId - The ID of the working time type to be associated with the procedure, or null if not applicable.
	 * @param {number | null} taskId - The ID of the task to be associated with the procedure, or null if not applicable.
	 * @param {boolean | null} toggleProjectTime - Flag indicating whether project time should be toggled, or null if not applicable.
	 * @return {void} - This method does not return a value.
	 */
	private switchCombinedRecording(procedure: RecordingProcedure, workingTimeTypeId: number | null, taskId: number | null, toggleProjectTime: boolean | null): void {
		// set loading state of switch panel
		this.setLoadingState(true);

		// add working time type id if working time is included in procedure
		const addWorkingTimeType = isWorkingTimeIncluded(procedure);
		if (addWorkingTimeType && !workingTimeTypeId) {
			this.setLoadingState(false);
			BootstrapModal.error({message: "combinedRecording.workingTimeTypeId.required"});
			return;
		}

		// add task id if procedure is project time or STRICT mode active or is to be started by 'start project time' switchery (attendance time is required)
		const addTaskId = this.shouldAddTaskIdForSwitch(procedure, toggleProjectTime);
		if (addTaskId && !taskId) {
			this.setLoadingState(false);
			BootstrapModal.error({message: "task.select.task"});
			return;
		}

		this.taskSwitchExecutor(addWorkingTimeType ? workingTimeTypeId : null, addTaskId ? taskId : null)
			.then(() => this.dialog.close());
	}

	/**
	 * Determines if the current dialog mode is set to "resume".
	 *
	 * @return {boolean} Returns true if the dialog mode is "resume"; otherwise, false.
	 */
	private isResumeMode(): boolean {
		return this.dialogMode === "resume";
	}

	/**
	 * Updates the visibility of action buttons based on the current dialog mode and recording procedure.
	 * The visibility of "switchButton" is determined by whether the dialog mode is "switch" and the procedure
	 * is not `RecordingProcedure.ProjectTime`. The visibility of "resumeButton" is determined by whether
	 * the dialog is in resume mode and the procedure is not `RecordingProcedure.ProjectTime`.
	 *
	 * @return {void} This method does not return a value.
	 */
	private updateActionButtonsVisibility(): void {
		this.dialog.toggleButton("switchButton", this.dialogMode === "switch" && this.procedure !== RecordingProcedure.ProjectTime);
		this.dialog.toggleButton("resumeButton", this.isResumeMode() && this.procedure !== RecordingProcedure.ProjectTime);
	}

	/**
	 * Retrieves the title for the dialog based on the current mode and procedure.
	 *
	 * @return {string} The computed dialog title based on the resume mode or the procedure type and end time.
	 */
	private getDialogTitle(): string {
		if (this.isResumeMode()) {
			return messages.withArguments("combinedRecording.switchDialog.title.resume", formatTime(this.endTime));
		}
		return messages.withArguments("combinedRecording.switchDialog.title." + this.procedure.toString(), formatTime(this.endTime));
	}

	/**
	 * Determines whether a task is mandatory for the current switch action.
	 *
	 * @param procedure Target switch procedure.
	 * @param toggleProjectTime User selection of "start project time" in non-STRICT mode.
	 */
	private shouldAddTaskIdForSwitch(procedure: RecordingProcedure, toggleProjectTime: boolean | null): boolean {
		return procedure === RecordingProcedure.ProjectTime
			|| (this.isAttendanceTimeSelected() && (toggleProjectTime || this.config.recordingMode === RecordingMode.STRICT));
	}
}
